import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Row, Col, Button, Form, Spinner, Card, Badge } from "react-bootstrap";
import axios from "axios";
import {
    FaPlus, FaPrescriptionBottle, FaThermometerHalf, FaBell,
    FaMoneyBillWave, FaTimes, FaCheck, FaEdit, FaTrash
} from "react-icons/fa";
import Select from "react-select";
import moment from "moment";
import Swal from "sweetalert2";

export default function PetDewormingComponent({ appointmentId, petId, isAppointmentOpen }) {
    const { t } = useContext(TranslatorContext);
    const API = process.env.REACT_APP_API_URL;
    const AuthToken = localStorage.getItem("authToken");
    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];
    const role = localStorage.getItem("role") || "";
    const hasPermission = (perm) => permissions.includes(perm);
    const isAdmin = role === "Admin";

    const [groups, setGroups] = useState([]);
    const [dewormingOptions, setDewormingOptions] = useState([]);
    const [loading, setLoading] = useState(true);

    const [completeState, setCompleteState] = useState({});
    const [addShotState, setAddShotState] = useState({});
    const [updateState, setUpdateState] = useState({});

    const showToast = (icon, title) => {
        Swal.mixin({
            toast: true, position: "top-end", showConfirmButton: false,
            timer: 3000, timerProgressBar: true,
        }).fire({ icon, title });
    };

    useEffect(() => {
        fetchData();
    }, [appointmentId, petId]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/pet-deworming/${petId}`, {
                headers: { Authorization: `Bearer ${AuthToken}` },
            });

            const list = response.data?.petDewormings || [];
            const standardProducts = response.data?.deworming || [];
            const inClinicProducts = response.data?.inClinicProducts || [];

            const options = [
                ...standardProducts.map(p => ({
                    value: p.id,
                    label: `${p.product_name} (${t("Stock")})`,
                    rawName: p.product_name,
                    sale_price: p.sale_price || 0,
                    stock: p.stock ?? 0,
                    in_clinic_stock: 0
                })),
                ...inClinicProducts.map(p => ({
                    value: p.id,
                    label: `${p.product_name} (${t("In-Clinic")})`,
                    rawName: p.product_name,
                    sale_price: p.price || 0,
                    stock: p.stock ?? 0,
                    in_clinic_stock: 1
                }))
            ];

            setDewormingOptions(options);
            setGroups(list);
        } catch (error) {
            console.error(error);
            showToast("error", t("failed_to_load_dewormings"));
        } finally {
            setLoading(false);
        }
    };

    const findDeworming = (id, fallbackName = "", inClinicStockValue = 0) => {
        let found = dewormingOptions.find(d => d.value === id && d.in_clinic_stock === inClinicStockValue);
        if (!found) {
            found = dewormingOptions.find(d => d.value === id);
        }
        if (!found && fallbackName) {
            found = dewormingOptions.find(d => d.rawName.toLowerCase() === fallbackName.toLowerCase() && d.in_clinic_stock === inClinicStockValue);
        }
        return found;
    };

    const getOptionsByStockType = (stockType) => {
        return dewormingOptions.filter(o => stockType === "in_clinic" ? o.in_clinic_stock === 1 : o.in_clinic_stock === 0);
    };

    const getAvailableOptionsByStockType = (stockType) => {
        return getOptionsByStockType(stockType).filter(
            option => Number(option.stock ?? 0) > 0
        );
    };

    const formatDewormingOptionLabel = (option) => (
        <div className="d-flex justify-content-between align-items-center w-100">
            <span className="fw-medium">{option.label}</span>
            <span className="small text-muted ms-2">
                {t("Stock")}:{" "}
                <strong className="text-dark">
                    {option.stock ?? 0}
                </strong>
            </span>
        </div>
    );

    const StockTypeSelector = ({ value, onChange, name }) => (
        <div className="d-flex gap-3 mb-2">
            <Form.Check
                type="radio"
                id={`${name}-stock`}
                name={name}
                label={t("Stock")}
                checked={value === "stock"}
                onChange={() => onChange("stock")}
            />
            <Form.Check
                type="radio"
                id={`${name}-in-clinic`}
                name={name}
                label={t("In-Clinic Stock")}
                checked={value === "in_clinic"}
                onChange={() => onChange("in_clinic")}
            />
        </div>
    );

    const hasPendingDewormings = groups.some(g => g.status === "reminder");
    const showInlineAddButton = !hasPendingDewormings && isAppointmentOpen && hasPermission("Appointment Deworming Create") && !addShotState.open;

    const handleDelete = async (groupId) => {
        const result = await Swal.fire({
            title: t("Are you sure?"),
            text: t("You won't be able to revert this deworming record!"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: t("Yes, delete it!"),
            cancelButtonText: t("Cancel")
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                await axios.delete(`${API}/pet-deworming/${groupId}`, {
                    headers: { Authorization: `Bearer ${AuthToken}` },
                });
                showToast("success", t("Deworming record deleted successfully."));
                fetchData();
            } catch (error) {
                console.error(error);
                showToast("error", error.response?.data?.message || t("failed_to_delete_deworming"));
                setLoading(false);
            }
        }
    };

    const openComplete = (group) => {
        setCompleteState(prev => ({
            ...prev,
            [group.group_id]: {
                open: true,
                temp: group.temp || "",
                deworming_date: group.deworming_date ? moment(group.deworming_date).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
                stock_type: "stock",
                rows: group.rows.map(r => {
                    const target = findDeworming(r.deworming_id, r.deworming_name, r.in_clinic_stock ?? 0);
                    return {
                        row_id: r.row_id ?? null,
                        deworming_id: target ? target.value : r.deworming_id,
                        deworming_name: target ? target.rawName : r.deworming_name,
                        price: r.price ?? (target?.sale_price || ""),
                        quantity: r.quantity ?? 1,
                        in_clinic_stock: r.in_clinic_stock ?? (target ? target.in_clinic_stock : 0),
                        stock: target?.stock ?? 0
                    };
                }),
                addReminder: false,
                reminder_date: "",
                reminder_stock_type: "stock",
                reminders: [],
                submitting: false,
            },
        }));
    };

    const closeComplete = (groupId) => {
        setCompleteState(prev => {
            const { [groupId]: _removed, ...rest } = prev;
            return rest;
        });
    };

    const updateCompleteField = (groupId, field, value) => {
        setCompleteState(prev => ({
            ...prev,
            [groupId]: { ...prev[groupId], [field]: value },
        }));
    };

    const updateCompleteStockType = (groupId, value) => {
        setCompleteState(prev => ({
            ...prev,
            [groupId]: { ...prev[groupId], stock_type: value, rows: [] },
        }));
    };

    const updateCompleteReminderStockType = (groupId, value) => {
        setCompleteState(prev => ({
            ...prev,
            [groupId]: { ...prev[groupId], reminder_stock_type: value, reminders: [] },
        }));
    };

    const updateCompleteRow = (groupId, dewormingId, inClinicStock, field, value) => {
        setCompleteState(prev => ({
            ...prev,
            [groupId]: {
                ...prev[groupId],
                rows: prev[groupId].rows.map(r =>
                    (r.deworming_id === dewormingId && r.in_clinic_stock === inClinicStock) ? { ...r, [field]: value } : r
                ),
            },
        }));
    };

    const updateCompleteDewormings = (groupId, selected) => {
        const selectedOptions = selected || [];
        setCompleteState(prev => {
            const current = prev[groupId];
            const rows = selectedOptions.map(opt => {
                const existing = current.rows.find(r => r.deworming_id === opt.value && r.in_clinic_stock === opt.in_clinic_stock);
                if (existing) return existing;
                return {
                    row_id: null,
                    deworming_id: opt.value,
                    deworming_name: opt.rawName,
                    price: opt.sale_price ?? "",
                    quantity: 1,
                    in_clinic_stock: opt.in_clinic_stock,
                    stock: opt.stock ?? 0
                };
            });
            return { ...prev, [groupId]: { ...current, rows } };
        });
    };

    const removeCompleteDeworming = (groupId, dewormingId, inClinicStock) => {
        setCompleteState(prev => ({
            ...prev,
            [groupId]: {
                ...prev[groupId],
                rows: prev[groupId].rows.filter(r => !(r.deworming_id === dewormingId && r.in_clinic_stock === inClinicStock)),
            },
        }));
    };

    const updateCompleteReminders = (groupId, selected) => {
        const selectedOptions = selected || [];
        setCompleteState(prev => ({
            ...prev,
            [groupId]: {
                ...prev[groupId],
                reminders: selectedOptions.map(opt => ({
                    deworming_id: opt.value,
                    in_clinic_stock: opt.in_clinic_stock
                })),
            },
        }));
    };

    const submitComplete = async (group) => {
        const data = completeState[group.group_id];
        if (!data) return;

        if (data.addReminder && (!data.reminder_date || data.reminders.length === 0)) {
            showToast("warning", t("please_select_reminder_dewormings_and_date"));
            return;
        }

        try {
            updateCompleteField(group.group_id, "submitting", true);

            const payload = {
                appointment_id: parseInt(appointmentId),
                pet_id: parseInt(petId),
                temp: data.temp || null,
                deworming_date: data.deworming_date,
                dewormings: data.rows.map(r => ({
                    row_id: r.row_id ?? null,
                    deworming_id: parseInt(r.deworming_id),
                    price: r.price === "" ? null : parseFloat(r.price),
                    quantity: parseInt(r.quantity) || 1,
                    in_clinic_stock: r.in_clinic_stock || 0
                })),
            };

            if (data.addReminder) {
                payload.reminder_date = data.reminder_date;
                payload.reminders = data.reminders.map(r => ({
                    deworming_id: parseInt(r.deworming_id),
                    in_clinic_stock: r.in_clinic_stock
                }));
            }

            await axios.post(`${API}/pet-deworming/complete/${group.group_id}`, payload, {
                headers: { Authorization: `Bearer ${AuthToken}` },
            });

            closeComplete(group.group_id);
            fetchData();
            showToast("success", t("Deworming completed successfully!"));
        } catch (error) {
            console.error(error);
            showToast("error", error.response?.data?.message || t("failed_to_complete_deworming"));
            updateCompleteField(group.group_id, "submitting", false);
        }
    };

    const openUpdate = (group) => {
        if (group.status === "completed") {
            setUpdateState(prev => ({
                ...prev,
                [group.group_id]: {
                    open: true,
                    status: "completed",
                    temp: group.temp ?? "",
                    deworming_date: group.deworming_date ? moment(group.deworming_date).format("YYYY-MM-DD") : "",
                    stock_type: "stock",
                    rows: group.rows.map(r => {
                        const target = findDeworming(r.deworming_id, r.deworming_name, r.in_clinic_stock ?? 0);
                        return {
                            row_id: r.row_id ?? null,
                            deworming_id: target ? target.value : r.deworming_id,
                            deworming_name: target ? target.rawName : r.deworming_name,
                            price: r.price ?? (target?.sale_price || ""),
                            quantity: r.quantity ?? 1,
                            in_clinic_stock: r.in_clinic_stock ?? (target ? target.in_clinic_stock : 0),
                            stock: target?.stock ?? 0
                        };
                    }),
                    addReminder: false,
                    reminder_date: "",
                    reminder_stock_type: "stock",
                    reminders: [],
                    submitting: false,
                },
            }));
        } else {
            setUpdateState(prev => ({
                ...prev,
                [group.group_id]: {
                    open: true,
                    status: "reminder",
                    reminder_date: group.deworming_date ? moment(group.deworming_date).format("YYYY-MM-DD") : "",
                    reminder_stock_type: "stock",
                    reminders: group.rows.map(r => {
                        const target = findDeworming(r.deworming_id, r.deworming_name, r.in_clinic_stock ?? 0);
                        return {
                            row_id: r.row_id ?? null,
                            deworming_id: target ? target.value : r.deworming_id,
                            in_clinic_stock: r.in_clinic_stock ?? (target ? target.in_clinic_stock : 0)
                        };
                    }),
                    submitting: false,
                },
            }));
        }
    };

    const closeUpdate = (groupId) => {
        setUpdateState(prev => {
            const { [groupId]: _removed, ...rest } = prev;
            return rest;
        });
    };

    const updateUpdateField = (groupId, field, value) => {
        setUpdateState(prev => ({
            ...prev,
            [groupId]: { ...prev[groupId], [field]: value },
        }));
    };

    const updateUpdateStockType = (groupId, value) => {
        setUpdateState(prev => ({
            ...prev,
            [groupId]: { ...prev[groupId], stock_type: value, rows: [] },
        }));
    };

    const updateUpdateReminderStockType = (groupId, value) => {
        setUpdateState(prev => ({
            ...prev,
            [groupId]: { ...prev[groupId], reminder_stock_type: value, reminders: [] },
        }));
    };

    const updateUpdateRow = (groupId, dewormingId, inClinicStock, field, value) => {
        setUpdateState(prev => ({
            ...prev,
            [groupId]: {
                ...prev[groupId],
                rows: prev[groupId].rows.map(r =>
                    (r.deworming_id === dewormingId && r.in_clinic_stock === inClinicStock) ? { ...r, [field]: value } : r
                ),
            },
        }));
    };

    const updateUpdateDewormings = (groupId, selected) => {
        const selectedOptions = selected || [];
        setUpdateState(prev => {
            const current = prev[groupId];
            const rows = selectedOptions.map(opt => {
                const existing = current.rows.find(r => r.deworming_id === opt.value && r.in_clinic_stock === opt.in_clinic_stock);
                if (existing) return existing;
                return {
                    row_id: null,
                    deworming_id: opt.value,
                    deworming_name: opt.rawName,
                    price: opt.sale_price ?? "",
                    quantity: 1,
                    in_clinic_stock: opt.in_clinic_stock,
                    stock: opt.stock ?? 0
                };
            });
            return { ...prev, [groupId]: { ...current, rows } };
        });
    };

    const removeUpdateDeworming = (groupId, dewormingId, inClinicStock) => {
        setUpdateState(prev => ({
            ...prev,
            [groupId]: {
                ...prev[groupId],
                rows: prev[groupId].rows.filter(r => !(r.deworming_id === dewormingId && r.in_clinic_stock === inClinicStock)),
            },
        }));
    };

    const updateUpdateReminderDewormings = (groupId, selected) => {
        const selectedOptions = selected || [];
        setUpdateState(prev => {
            const current = prev[groupId];
            const reminders = selectedOptions.map(opt => {
                const existing = current.reminders.find(r => r.deworming_id === opt.value && r.in_clinic_stock === opt.in_clinic_stock);
                if (existing) return existing;
                return {
                    row_id: null,
                    deworming_id: opt.value,
                    in_clinic_stock: opt.in_clinic_stock
                };
            });
            return { ...prev, [groupId]: { ...current, reminders } };
        });
    };

    const submitUpdate = async (group) => {
        const data = updateState[group.group_id];
        if (!data) return;

        if (data.status === "completed" && data.rows.length === 0) {
            showToast("warning", t("please_select_at_least_one_product"));
            return;
        }

        if (data.status === "completed" && data.addReminder && (!data.reminder_date || data.reminders.length === 0)) {
            showToast("warning", t("please_select_reminder_dewormings_and_date"));
            return;
        }

        if (data.status === "reminder" && (!data.reminder_date || data.reminders.length === 0)) {
            showToast("warning", t("please_select_reminder_dewormings_and_date"));
            return;
        }

        try {
            updateUpdateField(group.group_id, "submitting", true);

            let payload;

            if (data.status === "completed") {
                payload = {
                    pet_id: parseInt(petId),
                    appointment_id: appointmentId ? parseInt(appointmentId) : null,
                    temp: data.temp === "" ? null : data.temp,
                    deworming_date: data.deworming_date || null,
                    dewormings: data.rows.map(r => ({
                        row_id: r.row_id ?? null,
                        deworming_id: parseInt(r.deworming_id),
                        price: r.price === "" ? null : parseFloat(r.price),
                        quantity: parseInt(r.quantity) || 1,
                        in_clinic_stock: r.in_clinic_stock || 0
                    })),
                };

                if (data.addReminder) {
                    payload.reminder_date = data.reminder_date;
                    payload.reminders = data.reminders.map(r => ({
                        deworming_id: parseInt(r.deworming_id),
                        in_clinic_stock: r.in_clinic_stock
                    }));
                }
            } else {
                payload = {
                    pet_id: parseInt(petId),
                    reminder_date: data.reminder_date,
                    reminders: data.reminders.map(r => ({
                        row_id: r.row_id ?? null,
                        deworming_id: parseInt(r.deworming_id),
                        in_clinic_stock: r.in_clinic_stock
                    })),
                };
            }

            await axios.post(`${API}/pet-deworming/${group.group_id}`, payload, {
                headers: { Authorization: `Bearer ${AuthToken}` },
            });

            closeUpdate(group.group_id);
            fetchData();
            showToast("success", t("Deworming updated successfully!"));
        } catch (error) {
            console.error(error);
            showToast("error", error.response?.data?.message || t("failed_to_update_deworming"));
            updateUpdateField(group.group_id, "submitting", false);
        }
    };

    const openAddShot = () => {
        setAddShotState({
            open: true,
            deworming_date: moment().format("YYYY-MM-DD"),
            stock_type: "stock",
            dewormings: [],
            addReminder: false,
            reminder_date: "",
            reminder_stock_type: "stock",
            reminders: [],
            temp: "",
            submitting: false,
        });
    };

    const closeAddShot = () => setAddShotState({});
    const updateAddShotStateField = (field, value) => setAddShotState(prev => ({ ...prev, [field]: value }));

    const updateAddShotStockType = (value) => {
        setAddShotState(prev => ({ ...prev, stock_type: value, dewormings: [] }));
    };

    const updateAddShotReminderStockType = (value) => {
        setAddShotState(prev => ({ ...prev, reminder_stock_type: value, reminders: [] }));
    };

    const updateAddShotDewormings = (selected) => {
        const selectedOptions = selected || [];
        setAddShotState(prev => {
            const rows = selectedOptions.map(opt => {
                const existing = prev.dewormings.find(v => v.deworming_id === opt.value && v.in_clinic_stock === opt.in_clinic_stock);
                if (existing) return existing;
                return {
                    deworming_id: opt.value,
                    deworming_name: opt.rawName,
                    price: opt.sale_price ?? "",
                    quantity: 1,
                    in_clinic_stock: opt.in_clinic_stock,
                    stock: opt.stock ?? 0
                };
            });
            return { ...prev, dewormings: rows };
        });
    };

    const updateAddShotRow = (dewormingId, inClinicStock, field, value) => {
        setAddShotState(prev => ({
            ...prev,
            dewormings: prev.dewormings.map(v => (v.deworming_id === dewormingId && v.in_clinic_stock === inClinicStock) ? { ...v, [field]: value } : v),
        }));
    };

    const removeAddShotDeworming = (dewormingId, inClinicStock) => {
        setAddShotState(prev => ({ ...prev, dewormings: prev.dewormings.filter(v => !(v.deworming_id === dewormingId && v.in_clinic_stock === inClinicStock)) }));
    };

    const updateAddShotReminders = (selected) => {
        const selectedOptions = selected || [];
        setAddShotState(prev => ({
            ...prev,
            reminders: selectedOptions.map(opt => ({
                deworming_id: opt.value,
                in_clinic_stock: opt.in_clinic_stock
            }))
        }));
    };

    const submitAddShot = async () => {
        if (addShotState.dewormings.length === 0) {
            showToast("warning", t("please_select_at_least_one_product"));
            return;
        }

        if (addShotState.addReminder && (!addShotState.reminder_date || addShotState.reminders.length === 0)) {
            showToast("warning", t("please_select_reminder_dewormings_and_date"));
            return;
        }

        try {
            setAddShotState(prev => ({ ...prev, submitting: true }));
            const payload = {
                pet_id: parseInt(petId),
                appointment_id: appointmentId ? parseInt(appointmentId) : null,
                temp: addShotState.temp || null,
                deworming_date: addShotState.deworming_date,
                dewormings: addShotState.dewormings.map(v => ({
                    deworming_id: parseInt(v.deworming_id),
                    quantity: parseInt(v.quantity) || 1,
                    price: v.price === "" ? null : parseFloat(v.price),
                    in_clinic_stock: v.in_clinic_stock || 0
                })),
            };

            if (addShotState.addReminder) {
                payload.reminder_date = addShotState.reminder_date;
                payload.reminders = addShotState.reminders.map(r => ({
                    deworming_id: parseInt(r.deworming_id),
                    in_clinic_stock: r.in_clinic_stock
                }));
            }

            await axios.post(`${API}/pet-deworming/store`, payload, { headers: { Authorization: `Bearer ${AuthToken}` } });
            closeAddShot();
            fetchData();
            showToast("success", t("Deworming logged successfully!"));
        } catch (error) {
            console.error(error);
            showToast("error", error.response?.data?.message || t("failed_to_save_deworming"));
            setAddShotState(prev => ({ ...prev, submitting: false }));
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5 my-4">
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    const selectStyles = {
        control: (base, state) => ({ ...base, borderColor: state.isFocused ? '#86b7fe' : '#dee2e6', borderRadius: '0.375rem', fontSize: '0.9rem' }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    return (
        <div className="deworming-container">
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <h6 className="mb-0 text-dark-emphasis fw-bold text-uppercase">{t("Deworming History")}</h6>
            </div>

            <Row xs={1} sm={2} lg={3} className="g-3 align-items-start">
                {groups.map((group) => {
                    const complete = completeState[group.group_id];
                    const upd = updateState[group.group_id];

                    const completeOptions = complete?.open ? getAvailableOptionsByStockType(complete.stock_type) : [];
                    const completeReminderOptions = complete?.open ? getOptionsByStockType(complete.reminder_stock_type) : [];
                    const updateCompletedOptions = (upd?.open && upd.status === "completed") ? getOptionsByStockType(upd.stock_type) : [];
                    const updateReminderOptions = (upd?.open && upd.status === "reminder") ? getOptionsByStockType(upd.reminder_stock_type) : [];

                    const currentSelectedValues = complete?.open
                        ? completeOptions.filter(o => complete.rows.some(r => {
                            if (r.deworming_id && r.deworming_id === o.value && parseInt(r.in_clinic_stock ?? 0) === parseInt(o.in_clinic_stock)) return true;
                            const nameMatches = o.rawName.toLowerCase() === r.deworming_name?.toLowerCase();
                            const stockMatches = parseInt(o.in_clinic_stock) === parseInt(r.in_clinic_stock ?? 0);
                            return nameMatches && stockMatches;
                        }))
                        : [];

                    const currentUpdateSelectedValues = (upd?.open && upd.status === "completed")
                        ? updateCompletedOptions.filter(o => upd.rows.some(r => {
                            if (r.deworming_id && r.deworming_id === o.value && parseInt(r.in_clinic_stock ?? 0) === parseInt(o.in_clinic_stock)) return true;
                            const nameMatches = o.rawName.toLowerCase() === r.deworming_name?.toLowerCase();
                            const stockMatches = parseInt(o.in_clinic_stock) === parseInt(r.in_clinic_stock ?? 0);
                            return nameMatches && stockMatches;
                        }))
                        : [];

                    const currentUpdateReminderValues = (upd?.open && upd.status === "reminder")
                        ? updateReminderOptions.filter(o => upd.reminders.some(r => r.deworming_id === o.value && parseInt(r.in_clinic_stock ?? 0) === parseInt(o.in_clinic_stock)))
                        : [];

                    const currentUpdateReminderSelectedValues = (upd?.open && upd.status === "completed" && upd.addReminder)
                        ? updateCompletedOptions.filter(o => upd.reminders.some(r => r.deworming_id === o.value && parseInt(r.in_clinic_stock ?? 0) === parseInt(o.in_clinic_stock)))
                        : [];

                    const canUpdateReminder = group.status === "reminder" && isAdmin;
                    const canUpdateCompleted = group.status === "completed" && isAdmin;
                    const canDelete = isAdmin;

                    return (
                        <React.Fragment key={group.group_id}>
                            <Col>
                                <Card className={`shadow-sm border-0 border-start border-4 rounded-3 bg-white ${group.status === "completed" ? "border-success" : "border-warning"}`}>
                                    <Card.Body className="p-3 d-flex flex-column justify-content-between">
                                        <div>
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <Badge bg={group.status === "completed" ? "success-subtle" : "warning-subtle"} className={`${group.status === "completed" ? "text-success" : "text-warning-emphasis"} fw-semibold px-2 rounded-pill small`}>
                                                    {group.status === "completed" ? <FaCheck className="me-1" size={10} /> : <FaBell className="me-1" size={10} />}
                                                    {group.status === "completed" ? t("Completed Dose") : t("Reminder Setup")}
                                                </Badge>
                                                <div className="d-flex align-items-center gap-2">
                                                    {!complete?.open && !upd?.open && <span className="text-muted small fw-medium">{moment(group.deworming_date).format("DD-MMM-YYYY")}</span>}
                                                    {canDelete && !complete?.open && !upd?.open && (
                                                        <Button variant="link" className="text-danger p-0 border-0" onClick={() => handleDelete(group.group_id)}>
                                                            <FaTrash size={12} />
                                                        </Button>
                                                    )}
                                                    {canUpdateCompleted && !complete?.open && !upd?.open && (
                                                        <Button variant="link" className="text-dark p-0 border-0" onClick={() => openUpdate(group)}>
                                                            <FaEdit size={12} />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="mb-2 text-dark">
                                                <span className="fw-semibold text-dark-emphasis" style={{ fontSize: "0.95rem" }}><FaPrescriptionBottle className="text-secondary me-1" /> {group.dewormings}</span>
                                            </div>
                                        </div>

                                        {group.status === "completed" ? (
                                            <>
                                                {!upd?.open ? (
                                                    <div className="pt-2 border-top mt-3 d-flex justify-content-between text-muted small">
                                                        {group.temp && <div className="bg-light px-2 py-1 rounded-2"><FaThermometerHalf className="text-info me-1" />{group.temp}°C</div>}
                                                        {group.price !== null && <div className="fw-semibold text-success bg-success-subtle px-2 py-1 rounded-2"><FaMoneyBillWave className="me-1" />{group.price}</div>}
                                                    </div>
                                                ) : (
                                                    <div className="bg-white p-2 border rounded mt-2">
                                                        <Form.Group className="mb-2">
                                                            <Form.Label className="small fw-bold text-secondary mb-1">{t("Execution Date")}</Form.Label>
                                                            <Form.Control size="sm" type="date" value={upd.deworming_date} onChange={e => updateUpdateField(group.group_id, "deworming_date", e.target.value)} />
                                                        </Form.Group>
                                                        <Form.Group className="mb-2">
                                                            <Form.Label className="small fw-bold text-secondary mb-0">{t("Weight / Temp Target")}</Form.Label>
                                                            <Form.Control size="sm" type="number" step="0.1" value={upd.temp || ""} placeholder="°C" onChange={e => updateUpdateField(group.group_id, "temp", e.target.value)} />
                                                        </Form.Group>
                                                        <Form.Group className="mb-2">
                                                            <Form.Label className="small fw-bold text-secondary mb-0">{t("Select Treatments")}</Form.Label>
                                                            <StockTypeSelector
                                                                name={`upd-stock-${group.group_id}`}
                                                                value={upd.stock_type}
                                                                onChange={val => updateUpdateStockType(group.group_id, val)}
                                                            />
                                                            <Select
                                                                options={updateCompletedOptions}
                                                                value={currentUpdateSelectedValues}
                                                                onChange={sel => updateUpdateDewormings(group.group_id, sel)}
                                                                isMulti
                                                                styles={selectStyles}
                                                                getOptionValue={(option) => `${option.value}-${option.in_clinic_stock}`}
                                                                menuPortalTarget={document.body}
                                                            />
                                                        </Form.Group>

                                                        {upd.rows.map(row => {
                                                            const itemOption = findDeworming(row.deworming_id, "", row.in_clinic_stock);
                                                            const itemStock = row.stock ?? itemOption?.stock ?? 0;
                                                            const itemPrice = row.price || itemOption?.sale_price || 0;

                                                            return (
                                                                <div key={`${row.deworming_id}-${row.in_clinic_stock}`} className="bg-light rounded p-2 mb-2 border border-secondary-subtle position-relative">
                                                                    <div className="small fw-bold pe-3">{row.deworming_name || itemOption?.rawName} <small className="text-muted">({row.in_clinic_stock ? 'In-Clinic' : 'Stock'})</small></div>
                                                                    <Button variant="link" className="text-danger p-0 position-absolute end-0 top-0 mt-1 me-2" onClick={() => removeUpdateDeworming(group.group_id, row.deworming_id, row.in_clinic_stock)}><FaTimes size={12} /></Button>
                                                                    <Row g={2} className="mt-1">
                                                                        <Col><Form.Control size="sm" type="number" placeholder="Price" value={row.price} onChange={e => updateUpdateRow(group.group_id, row.deworming_id, row.in_clinic_stock, "price", e.target.value)} /></Col>
                                                                        <Col><Form.Control size="sm" type="number" placeholder="Qty" value={row.quantity} onChange={e => updateUpdateRow(group.group_id, row.deworming_id, row.in_clinic_stock, "quantity", e.target.value)} /></Col>
                                                                    </Row>
                                                                    <div className="mt-1 d-flex gap-2 align-items-center">
                                                                        {!row.in_clinic_stock && (
                                                                            <Badge bg="secondary" className="fw-normal" style={{ fontSize: "0.75rem" }}>
                                                                                {t("Stock")}: {itemStock}
                                                                            </Badge>
                                                                        )}
                                                                        <Badge bg="info-subtle" className="fw-semibold text-info-emphasis" style={{ fontSize: "0.75rem" }}>
                                                                            {t("Price")}: {itemPrice}
                                                                        </Badge>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}

                                                        {!hasPendingDewormings && (
                                                            <div
                                                                className={`d-flex align-items-center rounded px-3 py-2 my-3 ${upd.addReminder
                                                                    ? "bg-warning bg-opacity-25 border border-warning"
                                                                    : "bg-light border"
                                                                    }`}
                                                                style={{
                                                                    cursor: "pointer",
                                                                    transition: "all .2s ease",
                                                                }}
                                                                onClick={() =>
                                                                    updateUpdateField(group.group_id, "addReminder", !upd.addReminder)
                                                                }
                                                            >
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    checked={upd.addReminder || false}
                                                                    className="m-0"
                                                                    onChange={(e) => {
                                                                        e.stopPropagation();
                                                                        updateUpdateField(group.group_id, "addReminder", e.target.checked);
                                                                    }}
                                                                    label=""
                                                                />

                                                                <span
                                                                    className={`ms-2 fw-bold ${upd.addReminder ? "text-danger" : "text-primary"
                                                                        }`}
                                                                    style={{
                                                                        fontSize: "17px",
                                                                        userSelect: "none",
                                                                    }}
                                                                >
                                                                    {t("Schedule Next Deworming Follow Up?")}
                                                                </span>
                                                            </div>
                                                        )}

                                                        <div className="d-flex gap-1.5 mt-2 pt-2 border-top">
                                                            <Button variant="secondary" size="sm" className="w-50" onClick={() => closeUpdate(group.group_id)} disabled={upd.submitting}>{t("Cancel")}</Button>
                                                            <Button variant="primary" size="sm" className="w-50" onClick={() => submitUpdate(group)} disabled={upd.submitting}>
                                                                {upd.submitting ? <Spinner animation="border" size="sm" /> : t("Save")}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="mt-3 pt-2 border-top">
                                                {!complete?.open && !upd?.open ? (
                                                    <div className="d-flex gap-1.5">
                                                        {canUpdateReminder && (
                                                            <Button variant="primary" size="sm" className="w-50 fw-semibold rounded-3 py-1.5" onClick={() => openUpdate(group)}>
                                                                <FaEdit size={12} className="me-1" /> {t("Update")}
                                                            </Button>
                                                        )}
                                                        {isAppointmentOpen &&
                                                            hasPermission("Appointment Deworming edit") &&
                                                            (
                                                                <Button variant="success" size="sm" className={canUpdateReminder ? "w-50 fw-semibold rounded-3 py-1.5" : "w-100 fw-semibold rounded-3 py-1.5"} onClick={() => openComplete(group)}>
                                                                    <FaCheck size={12} className="me-1" /> {t("Mark Completed")}
                                                                </Button>
                                                            )}
                                                    </div>
                                                ) : upd?.open ? (
                                                    <div className="bg-white p-2 border rounded mt-2">
                                                        <Form.Group className="mb-3">
                                                            <Form.Label className="small fw-bold text-secondary mb-1">{t("Select Target Products")}</Form.Label>
                                                            <StockTypeSelector
                                                                name={`upd-reminder-stock-${group.group_id}`}
                                                                value={upd.reminder_stock_type}
                                                                onChange={val => updateUpdateReminderStockType(group.group_id, val)}
                                                            />
                                                            <Select
                                                                options={updateReminderOptions}
                                                                value={currentUpdateReminderValues}
                                                                isMulti
                                                                onChange={sel => updateUpdateReminderDewormings(group.group_id, sel)}
                                                                styles={selectStyles}
                                                                getOptionValue={(option) => `${option.value}-${option.in_clinic_stock}`}
                                                                menuPortalTarget={document.body}
                                                            />
                                                        </Form.Group>
                                                        <Form.Group className="mb-2">
                                                            <Form.Label className="small fw-bold text-secondary mb-1">{t("Reminder Date")}</Form.Label>
                                                            <Form.Control size="sm" type="date" value={upd.reminder_date} onChange={e => updateUpdateField(group.group_id, "reminder_date", e.target.value)} />
                                                        </Form.Group>

                                                        <div className="d-flex gap-1.5 mt-2 pt-2 border-top">
                                                            <Button variant="secondary" size="sm" className="w-50" onClick={() => closeUpdate(group.group_id)} disabled={upd.submitting}>{t("Cancel")}</Button>
                                                            <Button variant="primary" size="sm" className="w-50" onClick={() => submitUpdate(group)} disabled={upd.submitting}>
                                                                {upd.submitting ? <Spinner animation="border" size="sm" /> : t("Save")}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="bg-white p-2 border rounded mt-2">
                                                        <Form.Group className="mb-2">
                                                            <Form.Label className="small fw-bold text-secondary mb-1">{t("Execution Date")}</Form.Label>
                                                            <Form.Control size="sm" type="date" value={complete.deworming_date} onChange={e => updateCompleteField(group.group_id, "deworming_date", e.target.value)} />
                                                        </Form.Group>
                                                        <Form.Group className="mb-2">
                                                            <Form.Label className="small fw-bold text-secondary mb-0">{t("Weight / Temp Target")}</Form.Label>
                                                            <Form.Control size="sm" type="number" step="0.1" value={complete.temp || ""} placeholder="°C" onChange={e => updateCompleteField(group.group_id, "temp", e.target.value)} />
                                                        </Form.Group>
                                                        <Form.Group className="mb-2">
                                                            <Form.Label className="small fw-bold text-secondary mb-0">{t("Select Treatments")}</Form.Label>
                                                            <StockTypeSelector
                                                                name={`complete-stock-${group.group_id}`}
                                                                value={complete.stock_type}
                                                                onChange={val => updateCompleteStockType(group.group_id, val)}
                                                            />
                                                            <Select
                                                                options={completeOptions}
                                                                value={currentSelectedValues}
                                                                onChange={sel => updateCompleteDewormings(group.group_id, sel)}
                                                                isMulti
                                                                formatOptionLabel={formatDewormingOptionLabel}
                                                                styles={selectStyles}
                                                                getOptionValue={(option) => `${option.value}-${option.in_clinic_stock}`}
                                                                menuPortalTarget={document.body}
                                                            />
                                                        </Form.Group>

                                                        {complete.rows.map(row => {
                                                            const itemOption = findDeworming(row.deworming_id, "", row.in_clinic_stock);
                                                            const itemStock = row.stock ?? itemOption?.stock ?? 0;
                                                            const itemPrice = row.price || itemOption?.sale_price || 0;

                                                            return (
                                                                <div key={`${row.deworming_id}-${row.in_clinic_stock}`} className="bg-light rounded p-2 mb-2 border border-secondary-subtle position-relative">
                                                                    <div className="small fw-bold pe-3">{row.deworming_name || itemOption?.rawName} <small className="text-muted">({row.in_clinic_stock ? 'In-Clinic' : 'Stock'})</small></div>
                                                                    <Button variant="link" className="text-danger p-0 position-absolute end-0 top-0 mt-1 me-2" onClick={() => removeCompleteDeworming(group.group_id, row.deworming_id, row.in_clinic_stock)}><FaTimes size={12} /></Button>
                                                                    <Row g={2} className="mt-1">
                                                                        <Col><Form.Control size="sm" type="number" placeholder="Price" value={row.price} onChange={e => updateCompleteRow(group.group_id, row.deworming_id, row.in_clinic_stock, "price", e.target.value)} /></Col>
                                                                        <Col><Form.Control size="sm" type="number" placeholder="Qty" value={row.quantity} onChange={e => updateCompleteRow(group.group_id, row.deworming_id, row.in_clinic_stock, "quantity", e.target.value)} /></Col>
                                                                    </Row>
                                                                    <div className="mt-1 d-flex gap-2 align-items-center">
                                                                        {!row.in_clinic_stock && (
                                                                            <Badge bg="secondary" className="fw-normal" style={{ fontSize: "0.75rem" }}>
                                                                                {t("Stock")}: {itemStock}
                                                                            </Badge>
                                                                        )}
                                                                        <Badge bg="info-subtle" className="fw-semibold text-info-emphasis" style={{ fontSize: "0.75rem" }}>
                                                                            {t("Price")}: {itemPrice}
                                                                        </Badge>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}

                                                        <div
                                                            className={`d-flex align-items-center rounded px-3 py-2 my-3 ${complete.addReminder
                                                                ? "bg-warning bg-opacity-25 border border-warning"
                                                                : "bg-light border"
                                                                }`}
                                                            style={{
                                                                cursor: "pointer",
                                                                transition: "all .2s ease",
                                                            }}
                                                            onClick={() =>
                                                                updateCompleteField(group.group_id, "addReminder", !complete.addReminder)
                                                            }
                                                        >
                                                            <Form.Check
                                                                type="checkbox"
                                                                checked={complete.addReminder}
                                                                className="m-0"
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    updateCompleteField(
                                                                        group.group_id,
                                                                        "addReminder",
                                                                        e.target.checked
                                                                    );
                                                                }}
                                                                label=""
                                                            />

                                                            <span
                                                                className={`ms-2 fw-bold ${complete.addReminder ? "text-danger" : "text-primary"
                                                                    }`}
                                                                style={{
                                                                    fontSize: "17px",
                                                                    userSelect: "none",
                                                                }}
                                                            >
                                                                {t("Schedule Next Deworming Follow Up?")}
                                                            </span>
                                                        </div>

                                                        <div className="d-flex gap-1.5 mt-2 pt-2 border-top">
                                                            <Button variant="secondary" size="sm" className="w-50" onClick={() => closeComplete(group.group_id)} disabled={complete.submitting}>{t("Cancel")}</Button>
                                                            <Button variant="success" size="sm" className="w-50" onClick={() => submitComplete(group)} disabled={complete.submitting}>
                                                                {complete.submitting ? <Spinner animation="border" size="sm" /> : t("Save")}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            {complete?.open && complete.addReminder && (
                                <Col>
                                    <Card className="shadow-sm border-0 border-start border-4 border-info rounded-3 bg-white">
                                        <Card.Body className="p-3 d-flex flex-column justify-content-between">
                                            <div>
                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                    <Badge bg="info-subtle" className="text-info-emphasis fw-semibold px-2 rounded-pill small">
                                                        <FaBell className="me-1" size={10} /> {t("Next Reminder Setup")}
                                                    </Badge>
                                                    <Button variant="link" className="text-muted p-0 m-0" onClick={() => updateCompleteField(group.group_id, "addReminder", false)}>
                                                        <FaTimes size={14} />
                                                    </Button>
                                                </div>
                                                <div className="p-1 mt-2">
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="small fw-bold text-secondary mb-1">{t("Select Target Products")}</Form.Label>
                                                        <StockTypeSelector
                                                            name={`complete-reminder-stock-${group.group_id}`}
                                                            value={complete.reminder_stock_type}
                                                            onChange={val => updateCompleteReminderStockType(group.group_id, val)}
                                                        />
                                                        <Select options={completeReminderOptions} isMulti onChange={sel => updateCompleteReminders(group.group_id, sel)} styles={selectStyles} getOptionValue={(option) => `${option.value}-${option.in_clinic_stock}`} menuPortalTarget={document.body} />
                                                    </Form.Group>
                                                    <Form.Group className="mb-2">
                                                        <Form.Label className="small fw-bold text-secondary mb-1">{t("Reminder Date")}</Form.Label>
                                                        <Form.Control size="sm" type="date" value={complete.reminder_date} onChange={e => updateCompleteField(group.group_id, "reminder_date", e.target.value)} />
                                                    </Form.Group>
                                                </div>
                                            </div>
                                            <div className="text-muted small bg-light p-2 rounded border border-dashed mt-3">
                                                💡 {t("This reminder will save along with the completed dose form.")}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            )}

                            {upd?.open && upd.status === "completed" && upd.addReminder && (
                                <Col>
                                    <Card className="shadow-sm border-0 border-start border-4 border-info rounded-3 bg-white">
                                        <Card.Body className="p-3 d-flex flex-column justify-content-between">
                                            <div>
                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                    <Badge bg="info-subtle" className="text-info-emphasis fw-semibold px-2 rounded-pill small">
                                                        <FaBell className="me-1" size={10} /> {t("Next Reminder Setup")}
                                                    </Badge>
                                                    <Button variant="link" className="text-muted p-0 m-0" onClick={() => updateUpdateField(group.group_id, "addReminder", false)}>
                                                        <FaTimes size={14} />
                                                    </Button>
                                                </div>
                                                <div className="p-1 mt-2">
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="small fw-bold text-secondary mb-1">{t("Select Target Products")}</Form.Label>
                                                        <StockTypeSelector
                                                            name={`upd-reminder-stock-${group.group_id}`}
                                                            value={upd.reminder_stock_type}
                                                            onChange={val => updateUpdateReminderStockType(group.group_id, val)}
                                                        />
                                                        <Select
                                                            options={updateCompletedOptions}
                                                            isMulti
                                                            onChange={sel => updateUpdateReminderDewormings(group.group_id, sel)}
                                                            styles={selectStyles}
                                                            getOptionValue={(option) => `${option.value}-${option.in_clinic_stock}`}
                                                            menuPortalTarget={document.body}
                                                            value={currentUpdateReminderSelectedValues}
                                                        />
                                                    </Form.Group>
                                                    <Form.Group className="mb-2">
                                                        <Form.Label className="small fw-bold text-secondary mb-1">{t("Reminder Date")}</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="date"
                                                            value={upd.reminder_date || ""}
                                                            onChange={e => updateUpdateField(group.group_id, "reminder_date", e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </div>
                                            </div>
                                            <div className="text-muted small bg-light p-2 rounded border border-dashed mt-3">
                                                💡 {t("This reminder will save along with the update form.")}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            )}
                        </React.Fragment>
                    );
                })}

                {addShotState.open && (
                    <>
                        <Col>
                            <Card className="shadow-sm border-0 border-start border-4 border-success rounded-3 bg-white">
                                <Card.Body className="p-3 d-flex flex-column justify-content-between">
                                    <div>
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <Badge bg="success-subtle" className="text-success fw-semibold px-2 rounded-pill small">
                                                <FaPlus className="me-1" size={10} /> {t("Add Deworming Track")}
                                            </Badge>
                                        </div>

                                        <div className="bg-white p-2 border rounded mt-2">
                                            <Form.Group className="mb-2">
                                                <Form.Label className="small fw-bold text-secondary mb-1">{t("Treatment Date")}</Form.Label>
                                                <Form.Control size="sm" type="date" value={addShotState.deworming_date} onChange={e => updateAddShotStateField("deworming_date", e.target.value)} />
                                            </Form.Group>
                                            <Form.Group className="mb-2">
                                                <Form.Label className="small fw-bold text-secondary mb-0">{t("Temperature")} (°C)</Form.Label>
                                                <Form.Control size="sm" type="number" step="0.1" value={addShotState.temp} onChange={e => updateAddShotStateField("temp", e.target.value)} />
                                            </Form.Group>
                                            <Form.Group className="mb-2">
                                                <Form.Label className="small fw-bold text-secondary mb-0">{t("Select Treatments")}</Form.Label>
                                                <StockTypeSelector
                                                    name="addshot-stock"
                                                    value={addShotState.stock_type}
                                                    onChange={updateAddShotStockType}
                                                />
                                                <Select
                                                    options={getAvailableOptionsByStockType(addShotState.stock_type)}
                                                    value={getAvailableOptionsByStockType(addShotState.stock_type).filter(o => addShotState.dewormings.some(r => r.deworming_id === o.value && r.in_clinic_stock === o.in_clinic_stock))}
                                                    onChange={updateAddShotDewormings}
                                                    isMulti
                                                    formatOptionLabel={formatDewormingOptionLabel}
                                                    styles={selectStyles}
                                                    getOptionValue={(option) => `${option.value}-${option.in_clinic_stock}`}
                                                    menuPortalTarget={document.body}
                                                />
                                            </Form.Group>

                                            {addShotState.dewormings.map(v => {
                                                const itemOption = findDeworming(v.deworming_id, "", v.in_clinic_stock);
                                                const itemStock = v.stock ?? itemOption?.stock ?? 0;
                                                const itemPrice = v.price || itemOption?.sale_price || 0;

                                                return (
                                                    <div key={`${v.deworming_id}-${v.in_clinic_stock}`} className="bg-light rounded p-2 mb-2 border border-secondary-subtle position-relative">
                                                        <div className="small fw-bold pe-3">{v.deworming_name} <small className="text-muted">({v.in_clinic_stock ? 'In-Clinic' : 'Stock'})</small></div>
                                                        <Button variant="link" className="text-danger p-0 position-absolute end-0 top-0 mt-1 me-2" onClick={() => removeAddShotDeworming(v.deworming_id, v.in_clinic_stock)}><FaTimes size={12} /></Button>
                                                        <Row g={2} className="mt-1">
                                                            <Col><Form.Control size="sm" type="number" placeholder="Price" value={v.price} onChange={e => updateAddShotRow(v.deworming_id, v.in_clinic_stock, "price", e.target.value)} /></Col>
                                                            <Col><Form.Control size="sm" type="number" placeholder="Qty" value={v.quantity} onChange={e => updateAddShotRow(v.deworming_id, v.in_clinic_stock, "quantity", e.target.value)} /></Col>
                                                        </Row>
                                                        <div className="mt-1 d-flex gap-2 align-items-center">
                                                            {!v.in_clinic_stock && (
                                                                <Badge bg="secondary" className="fw-normal" style={{ fontSize: "0.75rem" }}>
                                                                    {t("Stock")}: {itemStock}
                                                                </Badge>
                                                            )}
                                                            <Badge bg="info-subtle" className="fw-semibold text-info-emphasis" style={{ fontSize: "0.75rem" }}>
                                                                {t("Price")}: {itemPrice}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            <div
                                                className={`d-flex align-items-center rounded px-3 py-2 my-3 ${addShotState.addReminder
                                                    ? "bg-warning bg-opacity-25 border border-warning"
                                                    : "bg-light border"
                                                    }`}
                                                style={{
                                                    cursor: "pointer",
                                                    transition: "all .2s ease",
                                                }}
                                                onClick={() =>
                                                    updateAddShotStateField(
                                                        "addReminder",
                                                        !addShotState.addReminder
                                                    )
                                                }
                                            >
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={addShotState.addReminder}
                                                    className="m-0"
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        updateAddShotStateField(
                                                            "addReminder",
                                                            e.target.checked
                                                        );
                                                    }}
                                                    label=""
                                                />

                                                <span
                                                    className={`ms-2 fw-bold ${addShotState.addReminder
                                                        ? "text-danger"
                                                        : "text-primary"
                                                        }`}
                                                    style={{
                                                        fontSize: "17px",
                                                        userSelect: "none",
                                                    }}
                                                >
                                                    {t("Schedule Next Deworming Follow Up?")}
                                                </span>
                                            </div>
                                            <div className="d-flex gap-1.5 mt-2 pt-2 border-top">
                                                <Button variant="secondary" size="sm" className="w-50" onClick={closeAddShot} disabled={addShotState.submitting}>{t("Cancel")}</Button>
                                                <Button variant="success" size="sm" className="w-50" onClick={submitAddShot} disabled={addShotState.submitting}>
                                                    {addShotState.submitting ? <Spinner animation="border" size="sm" /> : t("Save")}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        {addShotState.addReminder && (
                            <Col>
                                <Card className="shadow-sm border-0 border-start border-4 border-info rounded-3 bg-white">
                                    <Card.Body className="p-3 d-flex flex-column justify-content-between">
                                        <div>
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <Badge bg="info-subtle" className="text-info-emphasis fw-semibold px-2 rounded-pill small">
                                                    <FaBell className="me-1" size={10} /> {t("Next Due Setup")}
                                                </Badge>
                                                <Button variant="link" className="text-muted p-0 m-0" onClick={() => updateAddShotStateField("addReminder", false)}>
                                                    <FaTimes size={14} />
                                                </Button>
                                            </div>
                                            <div className="p-1 mt-2">
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="small fw-bold text-secondary mb-1">{t("Select Target Products")}</Form.Label>
                                                    <StockTypeSelector
                                                        name="addshot-reminder-stock"
                                                        value={addShotState.reminder_stock_type}
                                                        onChange={updateAddShotReminderStockType}
                                                    />
                                                    <Select options={getOptionsByStockType(addShotState.reminder_stock_type)} isMulti onChange={updateAddShotReminders} styles={selectStyles} getOptionValue={(option) => `${option.value}-${option.in_clinic_stock}`} menuPortalTarget={document.body} />
                                                </Form.Group>
                                                <Form.Group className="mb-2">
                                                    <Form.Label className="small fw-bold text-secondary mb-1">{t("Reminder Date")}</Form.Label>
                                                    <Form.Control size="sm" type="date" value={addShotState.reminder_date} onChange={e => updateAddShotStateField("reminder_date", e.target.value)} />
                                                </Form.Group>
                                            </div>
                                        </div>
                                        <div className="text-muted small bg-light p-2 rounded border border-dashed mt-3">
                                            💡 {t("This reminder will save along with the new deworming creation form.")}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        )}
                    </>
                )}

                {showInlineAddButton && (
                    <Col>
                        <Card
                            onClick={openAddShot}
                            className="shadow-sm border-2 border-dashed rounded-3 bg-white d-flex align-items-center justify-content-center text-center cursor-pointer p-4 transition-all hover-bg-light"
                            style={{ minHeight: "220px", cursor: "pointer", borderColor: "#cbd5e1" }}
                        >
                            <div className="text-muted">
                                <div className="bg-primary bg-opacity-10 text-primary rounded-circle d-inline-flex align-items-center justify-content-center mb-2" style={{ width: "45px", height: "45px" }}>
                                    <FaPlus size={18} />
                                </div>
                                <h6 className="fw-bold text-dark-emphasis mb-1">{t("Add Entry")}</h6>
                                <p className="small mb-0 opacity-75">{t("Configure Deworming Session")}</p>
                            </div>
                        </Card>
                    </Col>
                )}
            </Row>
        </div>
    );
}